import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { LinkCard } from "@amzn/stencil-react-components/card";
import { UtilityButton, UtilityButtonVariant } from "@amzn/stencil-react-components/dist/submodules/utility-button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { IconBin } from "@amzn/stencil-react-icons";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useLists } from "hooks/ListsContext";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { FC } from "react";
import { useHistory } from "react-router-dom";

export interface ListItemProps {
  listId: string;
  item: ListItem;
}

export interface ListItem {
  taxonomyId: string;
  name: string;
  description: string;
  logoUrl?: string;
}

export const ListItem: FC<ListItemProps> = ({ listId, item }) => {
  const history = useHistory();
  const { removeItemFromList } = useLists();
  const { addMessageBanner } = useMessageBannersBox();

  if (!item) {
    return (
      <View height="78px" width="100%" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="T200" color="neutral70">
          Item not found
        </Text>
      </View>
    );
  }
  const handleConfirmDelete = async (close: () => void) => {
    try {
      await removeItemFromList(listId, item.taxonomyId);
      addMessageBanner(getString("lists.removeItemSuccess"), MessageBannerType.Success);
      close();
    } catch (error) {
      addMessageBanner(getString("lists.removeItemError"), MessageBannerType.Error);
      close();
    }
  };

  const renderDeleteModal = ({ close }: RenderModalFunctionParams) => (
    <ModalContent
      titleText={getString("lists.deleteConfirmation.title")}
      buttons={[
        <Button key="cancel" variant={ButtonVariant.Secondary} onClick={close}>
          {getString("lists.deleteConfirmation.cancel")}
        </Button>,
        <Button key="confirm" variant={ButtonVariant.Primary} onClick={() => handleConfirmDelete(close)}>
          {getString("lists.deleteConfirmation.confirm")}
        </Button>,
      ]}
    >
      <Text>{getString("lists.deleteConfirmation.message", { name: item.name })}</Text>
    </ModalContent>
  );

  return (
    <LinkCard
      onClick={() => {
        if (ItemTaxonomyId.Parse(item.taxonomyId!).dataDomain != "aiforme")
          history.push(`/items/${ItemTaxonomyId.Parse(item.taxonomyId!).getUrlSuffix()}`);
      }}
      width={"100%"}
    >
      <Row height={"78px"} justifyContent="space-between" width={"100%"} gridGap="S250">
        {item.logoUrl && (
          <View
            display="flex"
            height={"78px"}
            padding={"S100"}
            alignItems="flex-start"
            className="img-square-wrapper"
            style={{ borderRadius: "16px" }}
          >
            <img
              alt={item.name}
              src={item.logoUrl}
              data-testid="catalog-item-url"
              style={{
                borderRadius: "16px",
                width: "70px",
                height: "70px",
                objectFit: "contain",
              }}
            />
          </View>
        )}
        <Col>
          <Text fontWeight="bold" fontSize="T200">
            {item.name}
          </Text>
          <Text
            fontSize="T100"
            style={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              height: "46px",
            }}
          >
            {item.description}
          </Text>
        </Col>
        <Col justifyContent="center">
          <WithModal renderModal={renderDeleteModal}>
            {({ open }) => (
              <UtilityButton
                icon={<IconBin color={"red70"} />}
                variant={UtilityButtonVariant.Tertiary}
                size={ButtonSize.Small}
                isDestructive
                onClick={async (e) => {
                  e.stopPropagation();
                  open();
                }}
              />
            )}
          </WithModal>
        </Col>
      </Row>
    </LinkCard>
  );
};
